import type { StatusMeta, Transition, WorkflowDefinition } from "./types";

/**
 * Turning a workflow definition into the status-flow graph.
 *
 * Kept free of React and of reactflow's runtime so it can be unit-tested directly — this is the
 * part with the logic in it, and the part where a layout regression would be invisible in a
 * screenshot review.
 */

/** Horizontal distance between two statuses sharing a layer. */
export const COLUMN_WIDTH = 240;

/** Vertical distance between layers. The flow reads top to bottom. */
export const ROW_HEIGHT = 130;

/** Fallback rank for a status the metadata does not rank. Mirrors the server's own fallback. */
export const UNRANKED_STATUS_RANK = 99;

export interface StatusNode {
    id: string;
    type: "status";
    position: { x: number; y: number };
    data: {
        status: string;
        rank: number;
        /** Distance in transitions from an entry status. Drives which row the node sits in. */
        layer: number;
        /** Transitions leaving this status, for the "no way out" hint. */
        outgoing: number;
        /** Transitions arriving at this status, for the "no path in" hint. */
        incoming: number;
    };
}

export interface TransitionEdge {
    id: string;
    source: string;
    target: string;
    label: string;
    /** MANUAL transitions are dashed; AUTO are solid. */
    animated: boolean;
    data: {
        transition: Transition;
    };
}

export interface Graph {
    nodes: StatusNode[];
    edges: TransitionEdge[];
}

/** Rank lookup from the metadata endpoint. Never hardcode this list client-side. */
export function rankLookup(statuses: StatusMeta[]): Map<string, number> {
    return new Map(statuses.map((status) => [status.value, status.rank]));
}

/**
 * A compact description of what a transition does, shown on its edge.
 *
 * Deliberately terse: the edge label has to stay readable at low zoom, and the detail panel
 * carries the full picture.
 */
export function edgeLabel(transition: Transition): string {
    const parts: string[] = [transition.transitionType];

    if (transition.priority) {
        parts.push(`p${transition.priority}`);
    }
    if (transition.condition) {
        parts.push("condition");
    }

    const actionCount = transition.actionConfigs.length;
    if (actionCount) {
        const ruleCount = transition.actionConfigs.reduce(
            (total, config) => total + config.rules.length,
            0,
        );
        parts.push(
            actionCount === 1 ? "1 action" : `${actionCount} actions`,
            ruleCount === 1 ? "1 rule" : `${ruleCount} rules`,
        );
    }

    return parts.join(" · ");
}

/**
 * Assign each status to a layer: its distance in transitions from an entry status.
 *
 * Layering is structural rather than taken from the status rank. Every status has a distinct
 * rank, so ranks alone would put every node on its own row and nothing beside anything — the
 * shape of the definition would be invisible. Distance from the entry is what puts the two
 * statuses reachable from NEW next to each other.
 *
 * Breadth-first, not longest-path: workflows contain cycles in practice (COMPLETED back to
 * WORK_IN_PROGRESS is normal), and longest-path layering is undefined on a cycle.
 *
 * ``rankOf`` breaks every tie, so the result depends only on the definition and not on
 * iteration order.
 */
export function assignLayers(
    statuses: string[],
    transitions: { fromStatus: string; toStatus: string }[],
    rankOf: (status: string) => number,
): Map<string, number> {
    const byRankThenName = (left: string, right: string) => {
        const byRank = rankOf(left) - rankOf(right);
        return byRank !== 0 ? byRank : left.localeCompare(right);
    };

    const successors = new Map<string, string[]>();
    const incomingCount = new Map<string, number>();
    for (const status of statuses) {
        successors.set(status, []);
        incomingCount.set(status, 0);
    }
    for (const transition of transitions) {
        successors.get(transition.fromStatus)?.push(transition.toStatus);
        incomingCount.set(
            transition.toStatus,
            (incomingCount.get(transition.toStatus) ?? 0) + 1,
        );
    }
    // Successors sorted so siblings enter their layer in a stable order.
    for (const list of successors.values()) {
        list.sort(byRankThenName);
    }

    const ordered = [...statuses].sort(byRankThenName);
    const layer = new Map<string, number>();
    const queue: string[] = [];

    const seed = (status: string, depth: number) => {
        if (!layer.has(status)) {
            layer.set(status, depth);
            queue.push(status);
        }
    };

    const drain = () => {
        while (queue.length) {
            const current = queue.shift() as string;
            const depth = layer.get(current) ?? 0;
            for (const next of successors.get(current) ?? []) {
                seed(next, depth + 1);
            }
        }
    };

    // Entry statuses: nothing transitions into them. Several is normal — a definition can have
    // more than one starting point, and they all belong on the top row.
    for (const status of ordered) {
        if ((incomingCount.get(status) ?? 0) === 0) {
            seed(status, 0);
        }
    }
    drain();

    // Whatever is left is only reachable within a cycle, so it has no natural entry. The
    // lowest-ranked member becomes one, and the loop repeats for any further disconnected cycle.
    while (layer.size < statuses.length) {
        const next = ordered.find((status) => !layer.has(status));
        if (next === undefined) {
            break;
        }
        seed(next, 0);
        drain();
    }

    return layer;
}

/**
 * Build the graph for a definition.
 *
 * One node per status the definition actually references, one edge per transition. The flow
 * reads top to bottom: each layer is a row, and statuses at the same distance from the entry sit
 * beside each other, so the branch structure is visible rather than flattened into a line.
 *
 * Layout is deterministic — the same definition always draws the same way. Explicitly not
 * force-directed: a layout that moves between loads makes the graph impossible to talk about.
 */
export function buildGraph(
    definition: WorkflowDefinition,
    statuses: StatusMeta[],
): Graph {
    const ranks = rankLookup(statuses);

    const outgoing = new Map<string, number>();
    const incoming = new Map<string, number>();
    const referenced = new Set<string>();

    for (const transition of definition.transitions) {
        referenced.add(transition.fromStatus);
        referenced.add(transition.toStatus);
        outgoing.set(
            transition.fromStatus,
            (outgoing.get(transition.fromStatus) ?? 0) + 1,
        );
        incoming.set(
            transition.toStatus,
            (incoming.get(transition.toStatus) ?? 0) + 1,
        );
    }

    const rankOf = (status: string) =>
        ranks.get(status) ?? UNRANKED_STATUS_RANK;

    // Sorted by rank, then name, so an unranked status still lands somewhere stable rather than
    // wherever set iteration happened to put it.
    const orderedStatuses = [...referenced].sort((left, right) => {
        const byRank = rankOf(left) - rankOf(right);
        return byRank !== 0 ? byRank : left.localeCompare(right);
    });

    const layers = assignLayers(
        orderedStatuses,
        definition.transitions,
        rankOf,
    );

    // Rows, each holding the statuses at that depth in rank order.
    const rows = new Map<number, string[]>();
    for (const status of orderedStatuses) {
        const depth = layers.get(status) ?? 0;
        rows.set(depth, [...(rows.get(depth) ?? []), status]);
    }

    const nodes: StatusNode[] = orderedStatuses.map((status) => {
        const depth = layers.get(status) ?? 0;
        const row = rows.get(depth) ?? [status];
        const indexInRow = row.indexOf(status);

        return {
            id: status,
            type: "status",
            // Rows are centred on x=0 so a branch reads as a fork rather than drifting right,
            // and depth drives y so the flow runs top to bottom.
            position: {
                x: (indexInRow - (row.length - 1) / 2) * COLUMN_WIDTH,
                y: depth * ROW_HEIGHT,
            },
            data: {
                status,
                rank: rankOf(status),
                layer: depth,
                outgoing: outgoing.get(status) ?? 0,
                incoming: incoming.get(status) ?? 0,
            },
        };
    });

    const edges: TransitionEdge[] = definition.transitions.map(
        (transition) => ({
            id: transition.id,
            source: transition.fromStatus,
            target: transition.toStatus,
            label: edgeLabel(transition),
            animated: transition.transitionType === "MANUAL",
            data: { transition },
        }),
    );

    return { nodes, edges };
}

/**
 * Structural problems visible from the graph alone.
 *
 * The engine logs a "stuck workflow" when an event reaches a status with no way out; surfacing
 * it here is cheaper than reading logs. Reported per status so the UI can mark the node.
 */
export function graphWarnings(graph: Graph): string[] {
    const warnings: string[] = [];

    for (const node of graph.nodes) {
        if (node.data.outgoing === 0) {
            warnings.push(
                `${node.data.status} has no transition out of it: an event that reaches it stops there.`,
            );
        }
        if (node.data.incoming === 0 && node.data.rank > 0) {
            warnings.push(
                `${node.data.status} has no transition into it, so nothing reaches it through this workflow.`,
            );
        }
    }

    return warnings;
}
